import type { RGBAColor } from '../deckgl-types';
import React from 'react';
import { Typography } from '@mui/material';
import { WebMercatorViewport } from '@deck.gl/core';
import { atom } from 'jotai';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { visualModeAtom, viewStateAtom, mapInteractingAtom } from '../atoms';
import type { Feature, GeoJsonProperties, Geometry, MultiPolygon, Polygon } from 'geojson';
import { useMatch, useSearchParams } from 'react-router-dom';
import { userAtom } from '../../auth/userAtoms';
import { Legend } from '../../components/Legend';
import { layerVisibility } from '../../map/atoms';
import { leafKeyForLayerId } from '../layerControls/layerRegistry';
import { PolygonLayer } from '@deck.gl/layers';
import { fetchFlatGeobuf, fetchFeatureCount } from '../../services/featuresApi';

/**
 * A row of reference.usa_structures as GET /api/features/usa_structures serves
 * it. The viewport fetch projects it to StructureFootprint; the whole row is
 * read only by the click popup, through useStructureDetail.
 */
export type Building = {
  /** Primary key of reference.usa_structures */
  globalid?: string | null;
  objectid?: number | null;
  /** Building ID */
  build_id?: number | null;
  /** Building Occupancy Classification */
  occ_cls?: string | null;
  /** Primary Occupancy */
  prim_occ?: string | null;
  /** Secondary Occupancy */
  sec_occ?: string | null;
  /** Address */
  prop_addr?: string | null;
  /** City */
  prop_city?: string | null;
  /** State */
  prop_st?: string | null;
  /** ZIP Code */
  prop_zip?: string | null;
  /** Outbuilding or Non-Primary Structure */
  outbldg?: string | null;
  /** Height in meters (stored as text in PostGIS) */
  height?: string | null;
  sqmeters?: number | null;
  sqfeet?: number | null;
  /** Highest elevation in meters of the ground adjacent to the structure */
  h_adj_elev?: string | null;
  /** Lowest elevation in meters of the ground adjacent to the structure */
  l_adj_elev?: string | null;
  /** County FIPS code */
  fips?: string | null;
  /** Census tract identifier */
  censuscode?: string | null;
  prod_date?: string | null;
  source?: string | null;
  /** United States National Grid (USNG) coordinates */
  usng?: string | null;
  longitude?: number | null;
  latitude?: number | null;
  image_name?: string | null;
  image_date?: string | null;
  /** Methodology used to validate building outline */
  val_method?: string | null;
  remarks?: string | null;
  uuid?: string | null;
  state_fips?: string | null;
  /** Name of building code */
  b_code?: string | null;
  /** Median building population estimate */
  pop_median?: string | null;
  /** Lower bound of 95% coverage interval on building population estimate */
  pop_ci95_lower?: string | null;
  /** Upper bound of 95% coverage interval on building population estimate */
  pop_ci95_upper?: string | null;
  /** County */
  prop_cnty?: string | null;
};

/**
 * What the viewport fetch carries per footprint: the primary key (the popup's
 * lookup key, see useStructureDetail), uuid (what exposed_structure_polygons
 * rows are keyed by), occ_cls (the occupancy colours) and height (3D
 * extrusion). Every other column is read only when a footprint is clicked, so
 * a bbox response over the >100M-row table pays for none of them.
 */
type StructureFootprint = Pick<Building, 'globalid' | 'uuid' | 'occ_cls' | 'height'>;

const STRUCTURE_FOOTPRINT_FIELDS: (keyof StructureFootprint)[] = [
  'globalid',
  'uuid',
  'occ_cls',
  'height',
];

export type StructureViewTypes = 'occupancy_type' | 'exposure' | 'none' | 'highlight';

// Minimum zoom for fetching/rendering structures. Below city scale the
// viewport holds more footprints than can be fetched and tesselated in a
// usable time (a zoom-12 viewport over downtown LA intersects ~306k rows of
// the 135M-row reference.usa_structures; zoom 13 quarters that), so the
// layer pages within a bounded budget and this floor keeps that budget
// covering most of the viewport.
export const STRUCTURES_MIN_ZOOM = 13;

// Server page size for the paged viewport fetch (matches FEATURES_PAGE_SIZE).
export const STRUCTURES_PAGE_SIZE = 10000;

// Ceiling on structures fetched for one viewport. Each 10k page costs ~0.5s
// to fetch+deserialize plus GPU tesselation, so an unbounded dense-metro
// viewport (LA ~306k) would stall for tens of seconds. Pages render
// progressively up to this budget and StructuresStatus reports the cutoff.
export const STRUCTURES_MAX_IN_VIEW = 50000;

// Extrusion fallback for rows whose text height column is null or non-numeric
const DEFAULT_STRUCTURE_HEIGHT_METERS = 20;

export const viewStructuresAtom = atom<{
  view: boolean;
  filter: StructureViewTypes;
}>({
  view: false,
  filter: 'occupancy_type',
});

// Pages of viewport structures, exploded to simple polygons. Page arrays are
// identity-stable once appended, so deck.gl tesselates each page exactly once
// and later pages render progressively without re-tesselating earlier ones.
const structurePagesAtom = atom<Feature<Polygon, StructureFootprint>[][]>([]);

type StructuresFetchStatus =
  | { state: 'idle' }
  | { state: 'loading'; count: number }
  | { state: 'loaded'; count: number; truncated: boolean }
  | { state: 'error' };

const structuresStatusAtom = atom<StructuresFetchStatus>({ state: 'idle' });

// Separate from structuresStatusAtom: the exposed-ids fetch runs in its own
// effect, and writing the paging status from it would race the paged loop.
const exposureIdsErrorAtom = atom<boolean>(false);

// Stable identity: reused wherever the exposed set must be emptied so the
// color accessors' updateTriggers don't churn on every effect run.
const EMPTY_ID_SET: ReadonlySet<string> = new Set();

/**
 * The event id of the last clicked event feature that exposure rows can be
 * keyed on. The Home map has no route or query parameter naming the WoW threat
 * or Canopy swath being looked at, so clicking one is how it is chosen; Map.tsx
 * records it from the popup's active feature.
 */
export const clickedExposureEventAtom = atom<string | undefined>(undefined);

// The event leaves (layerRegistry) whose features carry an
// exposed_structure_polygons event_id, and the column holding it: wow.py keys
// its exposure rows by threat_id, canopy.py by kml_id. nifc.py writes no
// exposure rows (it only counts), so a perimeter click selects nothing.
const EXPOSURE_EVENT_ID_COLUMN = new Map<string, string>([
  ['wow-forecast', 'threat_id'],
  ['wow-observed', 'threat_id'],
  ['canopy-extent', 'kml_id'],
]);

/**
 * What a picked feature means for the exposure selection: an event feature
 * selects its event, or clears the selection when its key column is empty --
 * wow.py assigns threat_id to only a few WoW categories, so the popup would
 * otherwise name one threat while the exposure views kept drawing another's.
 * A feature from any other layer (a footprint, a county, a POST cell) leaves
 * the selection alone.
 */
export type ExposureEventSelection =
  { kind: 'event'; eventId: string | undefined } | { kind: 'other' };

export const exposureEventForFeature = (
  layerId: string,
  properties: GeoJsonProperties
): ExposureEventSelection => {
  const leafKey = leafKeyForLayerId(layerId);
  const column = leafKey === null ? undefined : EXPOSURE_EVENT_ID_COLUMN.get(leafKey);
  if (column === undefined) {
    return { kind: 'other' };
  }
  const value = properties?.[column];
  return { kind: 'event', eventId: typeof value === 'string' && value !== '' ? value : undefined };
};

/**
 * Keeps clickedExposureEventAtom in step with the map: the popup's active
 * feature makes its selection (exposureEventForFeature), and a change of
 * resetKey -- the page, the primary layer or the ?canopy_kml_id= deep link,
 * each of which replaces the events on the map -- forgets it, so an earlier
 * click never names an event that is no longer drawn and the deep link is
 * never outranked by a click that predates it (useExposureEventId). Closing
 * the popup keeps the selection: clicking a footprint or an empty spot closes
 * it, and both are part of using the exposure views.
 */
export const useClickedExposureEvent = (
  activeFeature: { layerId: string; properties: GeoJsonProperties } | undefined,
  resetKey: string
): void => {
  const setClickedExposureEvent = useSetAtom(clickedExposureEventAtom);

  // Declared before the selection effect: effects run in declaration order, so
  // a commit that changes both the reset key and the active feature ends with
  // the newer click selected, not forgotten.
  React.useEffect(() => {
    setClickedExposureEvent(undefined);
  }, [resetKey, setClickedExposureEvent]);

  React.useEffect(() => {
    if (activeFeature === undefined) {
      return;
    }
    const selection = exposureEventForFeature(activeFeature.layerId, activeFeature.properties);
    if (selection.kind === 'event') {
      setClickedExposureEvent(selection.eventId);
    }
  }, [activeFeature, setClickedExposureEvent]);
};

/**
 * The event id that exposure rows are keyed by for the current view: an
 * incident route's id (which matches only when the incident is configured with
 * an event id), else the last clicked WoW threat or Canopy swath, else the
 * canopy alert a ?canopy_kml_id= deep link opened. The click outranks the deep
 * link because useClickedExposureEvent forgets the click whenever the deep
 * link changes, so a click is always the more recent choice of the two. The
 * deep link is only read while Canopy is the product on the map: MapPage
 * removes it from the URL when another product replaces Canopy.
 */
export const useExposureEventId = (): string | undefined => {
  const match = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });
  const [searchParams] = useSearchParams();
  const clickedEventId = useAtomValue(clickedExposureEventAtom);
  return (
    match?.params.incidentId ?? clickedEventId ?? searchParams.get('canopy_kml_id') ?? undefined
  );
};

export type StructureDetailStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'loaded'; properties: Building }
  | { state: 'error' };

const STRUCTURE_DETAIL_IDLE: StructureDetailStatus = { state: 'idle' };
const STRUCTURE_DETAIL_LOADING: StructureDetailStatus = { state: 'loading' };

/**
 * The whole reference.usa_structures row behind a picked footprint, requested
 * by primary key when the popup shows it. The viewport fetch carries only the
 * columns the layer draws with (STRUCTURE_FOOTPRINT_FIELDS), so the attributes
 * the popup lists arrive through this one indexed lookup instead of riding
 * along on every footprint in view. Idle without a primary key; a superseded
 * request (the popup stepping to another footprint) is aborted.
 */
export const useStructureDetail = (globalid: string | undefined): StructureDetailStatus => {
  const [result, setResult] = React.useState<{
    globalid: string;
    status: Extract<StructureDetailStatus, { state: 'loaded' | 'error' }>;
  } | null>(null);

  React.useEffect(() => {
    if (globalid === undefined) {
      return;
    }
    const abortController = new AbortController();

    fetchFlatGeobuf<Geometry, Building>('usa_structures', {
      filterField: 'globalid',
      filterValue: globalid,
      excludeGeometry: true,
      limit: 1,
      signal: abortController.signal,
    })
      .then(detailFC => {
        // No row means the footprint's key no longer exists in the reference
        // table (re-synced under the open map), which reads as a failed load
        // rather than a popup that silently lists nothing.
        const row = detailFC.features[0]?.properties;
        setResult({
          globalid,
          status: row == null ? { state: 'error' } : { state: 'loaded', properties: row },
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setResult({ globalid, status: { state: 'error' } });
        console.error('Structure detail fetch error:', error);
      });

    return () => {
      abortController.abort();
    };
  }, [globalid]);

  if (globalid === undefined) {
    return STRUCTURE_DETAIL_IDLE;
  }
  // Derived from the key rather than stored, so a footprint change never shows
  // the previous footprint's row while its own request is in flight.
  return result?.globalid === globalid ? result.status : STRUCTURE_DETAIL_LOADING;
};

/**
 * Whether the exposure views can be offered for the event: 'checking' while
 * the answer for it is outstanding, 'available' once exposure rows are known
 * to exist for it, 'unavailable' when there is no event, no signed-in user or
 * no rows, and 'error' when the check itself failed -- kept apart from
 * 'unavailable' so nothing claims the event has no data on the strength of a
 * request that never answered.
 */
export type ExposureAvailability = 'checking' | 'available' | 'unavailable' | 'error';

export const EXPOSURE_CHECK_FAILED_HINT =
  'Checking for structure exposure analysis data failed -- reload the page to retry';

/**
 * Checks exposed_structure_polygons for rows keyed by the event. The answer is
 * keyed by the event id and a superseded check is aborted: the id changes on
 * every event click, so a slow answer for the previous event must never enable
 * the views for the next one.
 */
export const useCheckStructuresData = (eventId: string | undefined): ExposureAvailability => {
  const [result, setResult] = React.useState<{
    eventId: string;
    availability: ExposureAvailability;
  } | null>(null);
  const user = useAtomValue(userAtom);

  React.useEffect(() => {
    if (!eventId || !user) {
      return;
    }
    const abortController = new AbortController();

    fetchFeatureCount('exposed_structure_polygons', {
      filterField: 'event_id',
      filterValue: eventId,
      signal: abortController.signal,
    })
      .then(count => {
        setResult({ eventId, availability: count > 0 ? 'available' : 'unavailable' });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setResult({ eventId, availability: 'error' });
        console.error('Exposure availability check failed:', error);
      });

    return () => {
      abortController.abort();
    };
  }, [eventId, user]);

  if (!eventId || !user) {
    return 'unavailable';
  }
  return result?.eventId === eventId ? result.availability : 'checking';
};

export const useStructuresLayer = () => {
  const [visualMode] = useAtom(visualModeAtom);
  const viewStructures = useAtomValue(viewStructuresAtom);

  const [structurePages, setStructurePages] = useAtom(structurePagesAtom);
  const [viewState] = useAtom(viewStateAtom);
  const isMapInteracting = useAtomValue(mapInteractingAtom);
  const exposureEventId = useExposureEventId();
  const [user] = useAtom(userAtom);
  const [exposedStructuresIds, setExposedStructuresIds] =
    React.useState<ReadonlySet<string>>(EMPTY_ID_SET);

  const [visible] = useAtom(layerVisibility('structures'));
  const setStructuresStatus = useSetAtom(structuresStatusAtom);

  // Rebuilt from the camera values so its identity only changes when the
  // camera actually moves -- the view state atom emits fresh object identities
  // for identical values, which would otherwise re-run the whole paged fetch
  // for the same viewport.
  const { latitude, longitude, zoom, pitch, bearing, altitude, width, height } = viewState;
  const cameraState = React.useMemo(
    () => ({ latitude, longitude, zoom, pitch, bearing, altitude, width, height }),
    [latitude, longitude, zoom, pitch, bearing, altitude, width, height]
  );

  const aboveMinZoom = (cameraState.zoom ?? 0) >= STRUCTURES_MIN_ZOOM;

  const viewportBbox = React.useMemo((): [number, number, number, number] => {
    const viewport = new WebMercatorViewport({
      ...cameraState,
      width: cameraState.width ?? document.documentElement.clientWidth,
      height: cameraState.height ?? document.documentElement.clientHeight,
    });
    const [minLng, minLat, maxLng, maxLat] = viewport.getBounds();
    return [minLng, minLat, maxLng, maxLat];
  }, [cameraState]);

  // Both the exposure filter and the highlight filter color by the
  // exposed-structure id set. Fetched separately from the structure pages so
  // switching filters recolors without re-paging the viewport.
  const needsExposureIds =
    user != null && (viewStructures.filter === 'exposure' || viewStructures.filter === 'highlight');

  const setExposureIdsError = useSetAtom(exposureIdsErrorAtom);

  // The exposed set belongs to one event: a newly selected event starts from
  // empty until its own rows arrive, instead of the previous event's set
  // colouring the footprints for the length of the fetch. Tracked while
  // rendering rather than from an effect, which would colour one frame with the
  // outgoing event's ids.
  const [idsEventId, setIdsEventId] = React.useState(exposureEventId);
  if (idsEventId !== exposureEventId) {
    setIdsEventId(exposureEventId);
    setExposedStructuresIds(EMPTY_ID_SET);
  }

  React.useEffect(() => {
    if (!viewStructures.view || !aboveMinZoom || isMapInteracting || !needsExposureIds) {
      return;
    }

    // Reset upfront so re-entering an exposure filter after a failure shows
    // the normal loading flow instead of flashing the stale error while the
    // automatic refetch is already in flight
    setExposureIdsError(false);

    // No event context means nothing can be exposed -- the set is already empty,
    // cleared above the moment the event changed. Fetching without the event
    // filter would return every event's rows (buildParams drops an empty
    // filterValue), mislabeling unrelated structures as exposed.
    if (!exposureEventId) {
      return;
    }

    const abortController = new AbortController();

    const fetchExposedIds = async () => {
      try {
        const exposedFC = await fetchFlatGeobuf<Polygon, { uuid: string }>(
          'exposed_structure_polygons',
          {
            filterField: 'event_id',
            filterValue: exposureEventId,
            bbox: viewportBbox,
            signal: abortController.signal,
          }
        );
        setExposedStructuresIds(new Set(exposedFC.features.map(f => f.properties.uuid)));
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setExposureIdsError(true);
        console.error('Exposed structures fetch error:', error);
      }
    };

    fetchExposedIds();

    return () => {
      abortController.abort();
    };
  }, [
    viewportBbox,
    aboveMinZoom,
    isMapInteracting,
    viewStructures.view,
    needsExposureIds,
    exposureEventId,
    setExposureIdsError,
  ]);

  React.useEffect(() => {
    // Defer while a drag/pinch/zoom gesture or camera transition is running:
    // the fetch fires from the effect re-run when mapInteractingAtom flips
    // false on release, with the camera the gesture settled on.
    if (!viewStructures.view || !aboveMinZoom || isMapInteracting) {
      return;
    }

    setStructuresStatus({ state: 'loading', count: 0 });
    const abortController = new AbortController();

    const fetchStructures = async () => {
      try {
        const bbox = viewportBbox;

        // Page through everything in view up to the budget, appending each
        // page as it arrives so structures render progressively. The first
        // page replaces the previous viewport's pages; earlier page arrays
        // keep their identity so deck.gl never re-tesselates them.
        const pages: Feature<Polygon, StructureFootprint>[][] = [];
        let fetched = 0;
        for (;;) {
          // reference.usa_structures geom is mixed Polygon/MultiPolygon
          const structuresFC = await fetchFlatGeobuf<Polygon | MultiPolygon, StructureFootprint>(
            'usa_structures',
            {
              bbox,
              fields: STRUCTURE_FOOTPRINT_FIELDS,
              limit: STRUCTURES_PAGE_SIZE,
              offset: fetched,
              signal: abortController.signal,
            }
          );

          const page: Feature<Polygon, StructureFootprint>[] = [];
          for (const feature of structuresFC.features) {
            if (feature.geometry.type === 'MultiPolygon') {
              // PolygonLayer tesselates one polygon per datum; feeding it a
              // MultiPolygon coordinate array produces NaN positions and kills
              // the whole layer, so multipart rows are exploded here.
              for (const polygonCoordinates of feature.geometry.coordinates) {
                page.push({
                  ...feature,
                  geometry: { type: 'Polygon', coordinates: polygonCoordinates },
                });
              }
            } else {
              page.push({ ...feature, geometry: feature.geometry });
            }
          }

          fetched += structuresFC.features.length;
          if (page.length > 0) {
            pages.push(page);
          }
          setStructurePages([...pages]);

          const lastPage = structuresFC.features.length < STRUCTURES_PAGE_SIZE;
          const budgetSpent = fetched >= STRUCTURES_MAX_IN_VIEW;
          if (lastPage || budgetSpent) {
            setStructuresStatus({
              state: 'loaded',
              count: fetched,
              truncated: !lastPage && budgetSpent,
            });
            return;
          }
          setStructuresStatus({ state: 'loading', count: fetched });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Pages fetched before the failure stay rendered: partial coverage
        // plus the error message beats blanking footprints the user can see.
        setStructuresStatus({ state: 'error' });
        console.error('Structures fetch error:', error);
      }
    };

    fetchStructures();

    return () => {
      abortController.abort();
    };
  }, [
    viewportBbox,
    aboveMinZoom,
    isMapInteracting,
    viewStructures.view,
    setStructurePages,
    setStructuresStatus,
  ]);

  const handleFillColor = React.useCallback(
    (f: Feature<Polygon, StructureFootprint>): RGBAColor => {
      if (viewStructures.filter === 'highlight') {
        // Exposed vs unexposed must differ in luminance and opacity, not hue
        // alone: the previous red-on-gray pair had near-identical luminance,
        // which is indistinguishable under red-green color deficiency.
        if (f.properties.uuid != null && exposedStructuresIds.has(f.properties.uuid)) {
          return [230, 57, 70, 235]; // Saturated red, near-opaque
        }
        return [220, 220, 220, 60]; // Pale gray, mostly transparent
      }

      switch (f.properties.occ_cls) {
        case 'Commercial':
          return [255, 159, 158, 140];
        case 'Industrial':
          return [163, 163, 163, 140];
        case 'Residential':
          return [255, 229, 110, 140];
        case 'Education':
          return [254, 173, 86, 140];
        case 'Government':
          return [79, 183, 218, 140];
        case 'Other':
          return [200, 160, 120, 140];
        default:
          return [255, 255, 255, 140];
      }
    },
    [viewStructures.filter, exposedStructuresIds]
  );

  const handleLineColor = React.useCallback(
    (f: Feature<Polygon, StructureFootprint>): RGBAColor => {
      if (viewStructures.filter === 'highlight') {
        // Dark outline on exposed, faint on unexposed -- see handleFillColor
        if (f.properties.uuid != null && exposedStructuresIds.has(f.properties.uuid)) {
          return [103, 9, 21, 255]; // Dark red, full opacity
        }
        return [158, 158, 158, 90]; // Faint gray
      }

      switch (f.properties.occ_cls) {
        case 'Commercial':
          return [255, 159, 158, 255];
        case 'Industrial':
          return [163, 163, 163, 255];
        case 'Residential':
          return [255, 229, 110, 255];
        case 'Education':
          return [254, 173, 86, 255];
        case 'Government':
          return [79, 183, 218, 255];
        case 'Other':
          return [200, 160, 120, 255];
        default:
          return [255, 255, 255, 255];
      }
    },
    [viewStructures.filter, exposedStructuresIds]
  );

  const layer = React.useMemo(
    () =>
      structurePages.map((page, i) => {
        // In occupancy/highlight modes each page keeps its identity, so
        // deck.gl reuses the tesselation of already-rendered pages when a
        // new page is appended.
        const features =
          viewStructures.filter === 'exposure'
            ? page.filter(
                f => f.properties.uuid != null && exposedStructuresIds.has(f.properties.uuid)
              )
            : page;

        const layer = new PolygonLayer<Feature<Polygon, StructureFootprint>>({
          id: `usa-structures-layer-${i}`,
          data: features,
          pickable: true,
          stroked: true,
          filled: true,
          getPolygon: d => d.geometry.coordinates,
          // lineWidthScale: 1,
          lineWidthMinPixels: 2,
          getLineColor: handleLineColor,
          getFillColor: handleFillColor,
          extruded: visualMode === '3d',
          visible: viewStructures.view && visible && aboveMinZoom,
          getElevation: f => {
            const height = parseFloat(f.properties.height ?? '');
            return Number.isFinite(height) ? height : DEFAULT_STRUCTURE_HEIGHT_METERS;
          },
          updateTriggers: {
            getElevation: [visualMode],
            getFillColor: [viewStructures.filter, exposedStructuresIds],
            getLineColor: [viewStructures.filter, exposedStructuresIds],
          },
        });

        return layer;
      }),
    [
      visible,
      aboveMinZoom,
      structurePages,
      viewStructures,
      visualMode,
      exposedStructuresIds,
      handleFillColor,
      handleLineColor,
    ]
  );

  return layer;
};

const occupancyTypeColors = {
  Commercial: [255, 159, 158],
  Industrial: [163, 163, 163],
  Residential: [255, 229, 110],
  Education: [254, 173, 86],
  Government: [79, 183, 218],
  Other: [200, 160, 120],
};

type StructuresStatusProps = {
  exposureEventId: string | undefined;
  exposureAvailability: ExposureAvailability;
};

/**
 * Status line shown while a structures view is active: tells the user why
 * nothing is drawn below STRUCTURES_MIN_ZOOM, that a fetch is in flight, how
 * many structures are on the map, and when the view is truncated at the
 * fetch cap -- so the toggle never appears to silently do nothing. With an
 * exposure view active it also says when no event is selected or the selected
 * one has no exposure rows, since the view outlives the event it was chosen
 * for and would otherwise show an empty or all-grey map under a plain count.
 */
export const StructuresStatus = React.memo(
  ({ exposureEventId, exposureAvailability }: StructuresStatusProps) => {
    const viewState = useAtomValue(viewStateAtom);
    const status = useAtomValue(structuresStatusAtom);
    const exposureIdsError = useAtomValue(exposureIdsErrorAtom);
    const viewStructures = useAtomValue(viewStructuresAtom);

    const exposureFilterActive =
      viewStructures.filter === 'exposure' || viewStructures.filter === 'highlight';

    let message: string;
    if ((viewState.zoom ?? 0) < STRUCTURES_MIN_ZOOM) {
      message = 'Zoom in to view structures';
    } else if (exposureIdsError && exposureFilterActive) {
      // Takes precedence over the paging status: with a failed exposed-ids
      // fetch the exposure filter renders nothing and highlight colors are
      // wrong, so a "Showing N structures" message would be misleading.
      message = 'Exposure data failed to load -- move the map to retry';
    } else if (exposureFilterActive && exposureAvailability === 'error') {
      message = EXPOSURE_CHECK_FAILED_HINT;
    } else if (exposureFilterActive && exposureEventId === undefined) {
      message =
        'No event selected -- click a WoW threat or Canopy swath that has structure ' +
        'exposure analysis data';
    } else if (exposureFilterActive && exposureAvailability === 'unavailable') {
      message = 'No structure exposure analysis data for the selected event';
    } else if (status.state === 'loading') {
      message =
        status.count > 0
          ? `Showing ${status.count.toLocaleString()} structures in view, loading more...`
          : 'Loading structures...';
    } else if (status.state === 'loaded') {
      message = status.truncated
        ? `Showing the first ${status.count.toLocaleString()} structures in view -- ` +
          'zoom in to see all'
        : `Showing ${status.count.toLocaleString()} structures in view`;
    } else if (status.state === 'error') {
      message = 'Structures failed to load -- move the map to retry';
    } else {
      return null;
    }

    return (
      <Typography variant="caption" sx={{ color: 'white', display: 'block', pb: 1 }}>
        {message}
      </Typography>
    );
  }
);

export const StructuresLegend = React.memo(() => {
  const generateOccupancyTypeLegendItems = () => {
    return Object.entries(occupancyTypeColors).map(([type, color]) => ({
      label: type,
      color: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
    }));
  };

  const occupancyTypeLegendItems = generateOccupancyTypeLegendItems();

  return (
    <Legend
      id="structures"
      leafKey="structures"
      title="Occupancy Type"
      items={occupancyTypeLegendItems}
    />
  );
});
